import "@fontsource-variable/inter/index.css"
import "@fontsource/ibm-plex-sans-arabic/400.css"
import "@fontsource/ibm-plex-sans-arabic/500.css"
import "@fontsource/ibm-plex-sans-arabic/600.css"
import "./styles.css"
import { NornRefusal } from "@norn/sdk"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider } from "@tanstack/react-router"
import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { LocaleProvider } from "./lib/i18n"
import { SessionProvider, unusableConfig } from "./lib/session"
import { loadShellConfig, type ShellConfig } from "./lib/shell-config"
import { applyTheme, loadTheme } from "./lib/theme"
import { router } from "./router"

applyTheme(loadTheme())

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // A refusal is an answer, not a transient failure: never retry it.
      retry: (failures, error) => !(error instanceof NornRefusal) && failures < 2,
    },
  },
})

const root = document.getElementById("root")
if (!root) throw new Error("#root missing from index.html")

function render(config: ShellConfig | null) {
  createRoot(root as HTMLElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <LocaleProvider>
          <SessionProvider config={config}>
            <RouterProvider router={router} />
          </SessionProvider>
        </LocaleProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}

// A deployment's shell configuration decides how operators sign in; a broken one is shown, never ignored.
loadShellConfig().then(
  (config) => {
    const unusable = unusableConfig(config)
    if (unusable) root.textContent = `This console cannot start: ${unusable}`
    else render(config)
  },
  (error: unknown) => {
    root.textContent = `This console's configuration (/norn-shell.json) could not be read: ${error instanceof Error ? error.message : String(error)}`
  },
)
